import {
  AbstractProduct, Attribute, DesignData, Product,
} from 'src/store/design/types';
import ensureCorrectProductUid from './ensureCorrectProductUid';
import findMugWrapAroundProduct from './findMugWrapAroundProduct';

/**
 * The single answer to "which product variant is this design bound to".
 *
 * Every request that carries a productId for a design — design add, design
 * update, cart add — must take it from here. Deriving it independently is how
 * the two ends drift apart: the design gets saved as one variant and the basket
 * line points at another, and production receives a UID that doesn't match the
 * files (e.g. a four-print-area UID for a design with one filled area).
 *
 * The rule chain, in order:
 *
 * 1. The bound uid is the one derived from the populated spreads
 *    (ensureCorrectProductUid), not the one selected on the product page — a
 *    customer can pick a four-print-area variant and fill a single area.
 * 2. Print-area preference first: mugs must be produced wrap-around, and those
 *    variants share a productUid with their one-side siblings, so the preferred
 *    sibling wins over a plain uid match. Products with no such sibling are
 *    left untouched.
 * 3. internalUid before productUid: siblings can share a productUid and differ
 *    only by internalUid, so a productUid match alone silently returns the
 *    first sibling.
 *
 * Returns undefined when nothing matches (no abstract product, no structure, or
 * a uid absent from the variant list) — callers keep their own fallback.
 */
const resolveBoundProduct = (
  abstractProduct: AbstractProduct | undefined,
  structure: DesignData | undefined,
): Product | undefined => {
  if (!abstractProduct || !structure) {
    return undefined;
  }

  const boundUid = ensureCorrectProductUid(structure).product_uid;

  if (!boundUid) {
    return undefined;
  }

  const products = abstractProduct.products || [];

  return findMugWrapAroundProduct(abstractProduct, boundUid)
    ?? products.find((product) => product.internalUid === boundUid)
    ?? products.find((product) => product.productUid === boundUid);
};

/**
 * The attribute bag to resolve sibling variants (another size, another colour)
 * against: the bound product's own attributes over the product-page selection,
 * so a sibling is picked from the variant the design is bound to.
 *
 * Takes the already-resolved product rather than resolving again, so a caller
 * that must not guess — anything sending a productId — can resolve once, check
 * the result, and refuse to continue without it. With no bound product this
 * falls back to the page selection, which is fine for display but is exactly
 * the guess that mismatched the design and the basket line, so callers on a
 * commit path guard instead of relying on the fallback.
 */
export const getVariantAttributes = (
  boundProduct: Product | undefined,
  selectedAttributes: { [typeName: string]: Attribute },
): { [typeName: string]: Attribute } => {
  if (!boundProduct) {
    return selectedAttributes;
  }

  const attributes = { ...selectedAttributes };
  boundProduct.attributes.forEach((attribute) => { attributes[attribute.typeName] = attribute; });
  return attributes;
};

export default resolveBoundProduct;
